import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Shuffle } from "lucide-react";
import { coverImgStyle, imgUrl, pickSurprise, useThemes } from "@/lib/inspiration";

const ThemeCard = ({ t }) => (
  <article data-testid={`inspire-card-${t.slug}`} className="flex w-[78%] shrink-0 snap-start flex-col overflow-hidden rounded-[6px] border border-[#B18A4B]/45 bg-[#FAF8F4] sm:w-[46%] lg:w-[calc((100%-3*1.25rem)/4)]">
    <h3 className="px-4 py-4 text-center font-serif-display text-[26px] font-medium leading-tight">{t.name}</h3>
    <div className="relative aspect-[3/4] overflow-hidden bg-[#EAE7E2]">
      <img src={imgUrl(t.cover.url)} alt={`Ambiente decorado con láminas: ${t.name}`} loading="lazy" className="h-full w-full object-cover" style={coverImgStyle(t.cover)} />
    </div>
    <div className="flex flex-1 flex-col items-center gap-4 px-4 py-5">
      <p className="text-center text-sm leading-relaxed text-[#080808]/70">{t.description}</p>
      <Link to={`/inspiracion/${t.slug}`} data-testid={`inspire-card-${t.slug}-button`}
        className="group mt-auto inline-flex items-center gap-2 rounded-full border border-[#080808] px-6 py-2.5 text-[0.8rem] font-medium transition-colors hover:bg-[#080808] hover:text-[#FAF8F4]">
        Ver ideas <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
      </Link>
    </div>
  </article>
);

export const InspireBlock = () => {
  const { data: themes = [] } = useThemes();
  const [first, setFirst] = useState(null);
  const [fading, setFading] = useState(false);
  const [edges, setEdges] = useState({ start: true, end: false });
  const track = useRef(null);

  const ordered = useMemo(() => {
    if (!first) return themes;
    return [...first.map((s) => themes.find((t) => t.slug === s)).filter(Boolean), ...themes.filter((t) => !first.includes(t.slug))];
  }, [themes, first]);

  const onScroll = () => {
    const el = track.current;
    if (el) setEdges({ start: el.scrollLeft < 8, end: el.scrollLeft + el.clientWidth >= el.scrollWidth - 8 });
  };
  useEffect(onScroll, [ordered]);

  const page = (dir) => track.current.scrollBy({ left: dir * track.current.clientWidth, behavior: "smooth" });
  const surprise = () => {
    const pick = pickSurprise(themes.map((t) => t.slug), ordered.slice(0, 4).map((t) => t.slug));
    setFading(true);
    setTimeout(() => {
      setFirst(pick);
      track.current.scrollTo({ left: 0, behavior: "auto" });
      setFading(false);
    }, 220);
  };

  if (!themes.length) return null;
  const arrow = "hidden lg:grid h-11 w-11 place-items-center rounded-full border border-[#080808]/20 transition-colors hover:border-[#080808] disabled:opacity-30";
  return (
    <section data-testid="inspire-section" aria-labelledby="inspire-title" className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12 py-20 sm:py-28">
      <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
        <div>
          <h2 id="inspire-title" className="font-serif-display text-[28px] sm:text-[40px] font-medium leading-tight">Inspírate con LUMINA</h2>
          <p className="mt-3 text-base text-[#080808]/70">Ideas para decorar con lo que más te inspira.</p>
        </div>
        <div className="flex gap-3">
          <button type="button" aria-label="Temáticas anteriores" data-testid="inspire-prev" disabled={edges.start} onClick={() => page(-1)} className={arrow}><ArrowLeft className="h-4 w-4" /></button>
          <button type="button" aria-label="Más temáticas" data-testid="inspire-next" disabled={edges.end} onClick={() => page(1)} className={arrow}><ArrowRight className="h-4 w-4" /></button>
        </div>
      </div>
      <div ref={track} onScroll={onScroll} data-testid="inspire-track"
        className={`-mx-5 mt-10 flex snap-x snap-mandatory gap-5 overflow-x-auto scroll-px-5 px-5 pb-4 transition-opacity duration-200 [scrollbar-width:none] sm:mx-0 sm:scroll-px-0 sm:px-0 [&::-webkit-scrollbar]:hidden ${fading ? "opacity-0" : "opacity-100"}`}>
        {ordered.map((t) => <ThemeCard key={t.slug} t={t} />)}
      </div>
      <div className="mt-10 flex justify-center">
        <button type="button" data-testid="inspire-surprise" onClick={surprise}
          className="inline-flex items-center gap-3 rounded-full bg-[#B18A4B] px-9 py-4 text-[0.8rem] font-semibold uppercase tracking-[0.16em] text-[#FAF8F4] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-[#080808]">
          <Shuffle className="h-4 w-4" /> Sorpréndeme
        </button>
      </div>
    </section>
  );
};
